import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { convertOdds } from '../utils/oddsConverter'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import { saveUserFilter, getUserFilters, deleteUserFilter, setDefaultFilter, unsetDefaultFilter, getDefaultFilter } from '../services/userDataService'
import FilterPanel from './FilterPanel'
import HiddenBetsModal, { useHiddenBets } from './HiddenBetsModal'
import TrackBetModal, { useTrackBet } from './TrackBetModal'
import PropEvMultiBuilder from './PropEvMultiBuilder'
import { settlementFields } from '../TRACKER_PAGE/settlement'
import { buildMarketExcluder, DEFAULT_FILTERS, MARKET_AVG_TIP } from './propEvConfig'
import ShareModal from '../EV_NEW_PAGE/ShareModal'
import PropEvTour, { openPropEvTour } from './propEvTour'
import '../styles/app.css'
import './PropEV.css'
import PreviewWall from '../components/PreviewWall'
import { playerImageUrlFromSelection as getPlayerImageUrl } from '../config/playerImages'
import useVisiblePolling from '../hooks/useVisiblePolling'

// ─── Constants ───────────────────────────────────────────────────────────────

const MARKET_TYPE_NAMES = {
  player_disposals:                        'Disposals',
  player_goals:                            'Goals',
  player_marks:                            'Marks',
  player_tackles:                          'Tackles',
  player_kicks:                            'Kicks',
  player_handballs:                        'Handballs',
  player_clearances:                       'Clearances',
  player_disposals_milestones:             'Disposals Milestones',
  player_goals_milestones:                 'Goals Milestones',
  player_marks_milestones:                 'Marks Milestones',
  player_tackles_milestones:               'Tackles Milestones',
  player_kicks_milestones:                 'Kicks Milestones',
  player_handballs_milestones:             'Handballs Milestones',
  player_clearances_milestones:            'Clearances Milestones',
  player_tries_milestones:                 'Tries Milestones',
  player_points_milestones:                'Points Milestones',
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatMarketType = (mt) =>
  MARKET_TYPE_NAMES[mt] ||
  mt.replace('player_', '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bYds\b/, 'Yards').replace(/\bTds\b/, 'TDs')

const getHoursUntilGame = (ts) => {
  const diff = new Date(ts + 'Z') - new Date()
  return Math.max(0, diff / (1000 * 60 * 60))
}

const formatDateTime = (ts) =>
  new Date(ts + 'Z').toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })

const formatTimeWithSeconds = (ts) => {
  if (!ts) return ''
  return new Date(ts + 'Z').toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true,
  })
}

const parseSelection = (sel) => {
  const m = sel.match(/^(.+?)\s+(over|under)\s+(.+)$/i)
  if (!m) return { name: sel, dir: null, line: null }
  return { name: m[1], dir: m[2].toLowerCase(), line: m[3] }
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function PropEV({ isVisible = true, isPreview = false }) {
  const { user, oddsFormat, hasAccess, hadTrial, loading: authLoading } = useAuth()
  isPreview = isPreview || authLoading || !hasAccess('prop_ev')

  const [opportunities,         setOpportunities]         = useState([])
  const [filteredOpportunities, setFilteredOpportunities] = useState([])
  const [loading,               setLoading]               = useState(true)
  const [error,                 setError]                 = useState(null)
  const [lastUpdate,            setLastUpdate]            = useState(null)
  const [expandedRows,          setExpandedRows]          = useState(new Set())
  const [autoRefreshEnabled,    setAutoRefreshEnabled]    = useState(false)
  const [shareBet,              setShareBet]              = useState(null)
  const { hiddenBets, hideOpp, unhideOpp, clearAll, isHidden, showModal, setShowModal } = useHiddenBets()
  const [showFilterPanel,       setShowFilterPanel]       = useState(false)
  const [sortBy,                setSortBy]                = useState('ev_percentage')
  const [sortDir,               setSortDir]               = useState('desc')
  const [currentPage,           setCurrentPage]           = useState(1)
  const [atBottom,   setAtBottom]   = useState(false)
  const itemsPerPage = 25

  const [filters,       setFilters]       = useState(DEFAULT_FILTERS)
  const [kellyFraction, setKellyFraction] = useState('1/4')
  const [bankroll,      setBankroll]      = useState(1000)
  const [roundStakes,   setRoundStakes]   = useState('5')

  const { isTracked, openTrackModal, closeTrackModal, saveTrack, showTrackModal, trackingOpp, trackForm, setTrackForm } =
    useTrackBet({ kellyFraction, bankroll, roundStakes, hideOpp })

  const [savedFilters,        setSavedFilters]        = useState([])
  const [filterName,          setFilterName]          = useState('')
  const [defaultFilterLoaded, setDefaultFilterLoaded] = useState(false)
  const [activeFilterId,      setActiveFilterId]      = useState(null)

  // Multi builder
  const [showMultiModal, setShowMultiModal] = useState(false)
  const [multiLegs,      setMultiLegs]      = useState([])

  const [noteDismissed, setNoteDismissed] = useState(() => localStorage.getItem('propEvNoteDismissed') === '1')
  const dismissNote = () => { localStorage.setItem('propEvNoteDismissed', '1'); setNoteDismissed(true) }
  const [pickerState,    setPickerState]    = useState(null)   // { opp, x, y }
  const [multiError,     setMultiError]     = useState(null)   // { key, x, y }

  // ─── Derived ───────────────────────────────────────────────────────────────

  const groupedOpportunities = useMemo(() => {
    const groups = new Map()
    filteredOpportunities.forEach(opp => {
      const key = `${opp.selection}|${opp.market_type}|${opp.commence_time}|${opp.original_odds}`
      if (groups.has(key)) {
        groups.get(key).bookmakers.push({ name: opp.bookmaker, game_url: opp.game_url })
      } else {
        groups.set(key, { ...opp, bookmakers: [{ name: opp.bookmaker, game_url: opp.game_url }] })
      }
    })
    return [...groups.values()]
  }, [filteredOpportunities])

  // ─── Stake ─────────────────────────────────────────────────────────────────

  const computeStakeAmount = (kellyStake) => {
    if (typeof kellyStake !== 'number') return null
    const fractions = { '1/4': 0.25, '1/2': 0.5, '3/4': 0.75, 'Full': 1 }
    const frac = fractions[kellyFraction] ?? 0.5
    const stake = (kellyStake / 100) * frac * bankroll
    if (roundStakes === '1') return Math.round(stake)
    if (roundStakes === '5') return Math.round(stake / 5) * 5
    return Math.round(stake * 100) / 100
  }

  const computeStake = (kellyStake) => {
    const amt = computeStakeAmount(kellyStake)
    return amt == null ? '' : `$${amt}`
  }

  const evBadgeClass = (opp) => {
    const ev = opp.ev_percentage
    if (ev >= 10) return 'bc-ev-badge bc-ev-high'
    if (ev >= 5)  return 'bc-ev-badge bc-ev-mid'
    return 'bc-ev-badge bc-ev-low'
  }

  // ─── Multi Builder helpers ────────────────────────────────────────────────

  const multiLegKey = (opp) =>
    `${opp.selection}|${opp.market_type}|${opp.commence_time}|${opp.original_odds}`

  const isInMulti   = (opp) => multiLegs.some(l => l._key === multiLegKey(opp))
  const lockedBook  = multiLegs.length > 0 ? multiLegs[0].bookmaker : null

  const addMultiLeg = (opp, bookmakerName) => {
    const bm = opp.bookmakers.find(b => b.name === bookmakerName)
    setMultiLegs(prev => [...prev, {
      _key:         multiLegKey(opp),
      selection:    opp.selection,
      match:        opp.match,
      market_type:  opp.market_type,
      sport:        opp.sport || 'AFL',
      commence_time: opp.commence_time,
      bookmaker:    bookmakerName,
      game_url:     bm?.game_url || null,
      odds:         opp.original_odds,
      ev_percentage: opp.ev_percentage,
      fair_odds:    opp.fair_odds,
      // Teams and the raw values behind the selection, so a multi's legs can be
      // settled against results without parsing the display string back apart.
      ...settlementFields(opp),
    }])
  }

  const handleMultiLegClick = (opp, buttonEl) => {
    const key = multiLegKey(opp)

    // Toggle off if already in multi
    if (isInMulti(opp)) {
      setMultiLegs(prev => prev.filter(l => l._key !== key))
      return
    }

    // Correlation check — same player + same base stat type + same game already in multi
    const oppPlayer   = opp.selection.match(/^(.+?)\s+(over|under)\s+/i)?.[1]?.trim() || ''
    const oppBaseStat = opp.market_type.replace('_milestones', '')
    const correlated  = oppPlayer && multiLegs.some(l => {
      const lPlayer   = l.selection.match(/^(.+?)\s+(over|under)\s+/i)?.[1]?.trim() || ''
      const lBaseStat = l.market_type.replace('_milestones', '')
      return lPlayer === oppPlayer && lBaseStat === oppBaseStat && l.commence_time === opp.commence_time
    })
    if (correlated) {
      const rect = buttonEl.getBoundingClientRect()
      setMultiError({ key, msg: `⚠ Correlated: ${oppPlayer} already has a ${oppBaseStat.replace('player_', '').replace(/_/g, ' ')} leg`, x: rect.left + rect.width / 2, y: rect.top - 2 })
      setTimeout(() => setMultiError(null), 2800)
    }

    // First leg — auto-add if only one bookmaker, otherwise show picker
    if (multiLegs.length === 0) {
      if (opp.bookmakers.length === 1) {
        addMultiLeg(opp, opp.bookmakers[0].name)
      } else {
        const rect = buttonEl.getBoundingClientRect()
        setPickerState({ opp, x: rect.left, y: rect.bottom + 4 })
      }
      return
    }

    // Subsequent legs — validate locked bookmaker
    if (!opp.bookmakers.some(b => b.name === lockedBook)) {
      const rect = buttonEl.getBoundingClientRect()
      setMultiError({ key, msg: `Not on ${lockedBook}`, x: rect.left + rect.width / 2, y: rect.top - 2 })
      setTimeout(() => setMultiError(null), 2500)
      return
    }

    addMultiLeg(opp, lockedBook)
  }

  // ─── Fetch ─────────────────────────────────────────────────────────────────

  const fetchOpportunities = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const { data, error } = await supabase.functions.invoke('one-sided-ev-proxy')
      if (error) throw error
      setOpportunities(data.opportunities || [])
      setLastUpdate(new Date())
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => { fetchOpportunities() }, [])

  useEffect(() => {
    if (!user) return
    getUserFilters(user.id, 'prop_ev').then(({ data }) => {
      if (data) setSavedFilters(data.map(f => ({ id: f.id, name: f.filter_name, isDefault: f.is_default || false, ...f.filter_data })))
    })
  }, [user])

  useEffect(() => {
    if (!user || defaultFilterLoaded) return
    getDefaultFilter(user.id, 'prop_ev').then(({ data }) => {
      if (data) applyFilterData(data.filter_data)
      setDefaultFilterLoaded(true)
    })
  }, [user, defaultFilterLoaded])

  useVisiblePolling(() => fetchOpportunities(true), 30000, autoRefreshEnabled && isVisible)

  useEffect(() => {
    const isMarketExcluded = buildMarketExcluder(filters.excludedMarketTypes)

    const filtered = opportunities.filter(opp => {
      if (filters.excludedSports.size > 0 && filters.excludedSports.has(opp.sport || 'AFL')) return false
      if (filters.excludedBookmakers.size > 0 && filters.excludedBookmakers.has(opp.bookmaker)) return false
      if (isMarketExcluded(opp)) return false
      if (opp.original_odds < filters.odds[0] || opp.original_odds > filters.odds[1]) return false
      if (opp.ev_percentage < filters.ev[0] || opp.ev_percentage > filters.ev[1]) return false
      const hours = getHoursUntilGame(opp.commence_time)
      if (hours < filters.hoursUntil[0] || hours > filters.hoursUntil[1]) return false
      if ((opp.bookmaker_count || 1) < filters.minBooks) return false
      return true
    })

    const sorted = [...filtered].sort((a, b) => {
      let av, bv
      if (sortBy === 'ev_percentage') { av = a.ev_percentage; bv = b.ev_percentage }
      else if (sortBy === 'original_odds')  { av = a.original_odds;  bv = b.original_odds }
      else if (sortBy === 'win_probability'){ av = a.win_probability; bv = b.win_probability }
      else if (sortBy === 'kelly_stake')    { av = a.kelly_stake;     bv = b.kelly_stake }
      else if (sortBy === 'bookmaker_count'){ av = a.bookmaker_count; bv = b.bookmaker_count }
      else if (sortBy === 'commence_time')  {
        av = new Date(a.commence_time + 'Z').getTime()
        bv = new Date(b.commence_time + 'Z').getTime()
      }
      else { av = 0; bv = 0 }
      return sortDir === 'desc' ? bv - av : av - bv
    })

    setFilteredOpportunities(sorted)
    setCurrentPage(1)
  }, [opportunities, filters, sortBy, sortDir, kellyFraction, bankroll, roundStakes])

  useEffect(() => {
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement
      setAtBottom(scrollHeight - scrollTop - clientHeight < 60)
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // tick forces a re-render every 10s so the live-dot freshness re-evaluates
  // eslint-disable-next-line no-unused-vars
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10000)
    return () => clearInterval(id)
  }, [])

  const dataAge = lastUpdate ? (new Date() - lastUpdate) / 1000 : null
  const dotClass = !lastUpdate ? 'bc-live-dot' : dataAge < 60 ? 'bc-live-dot' : 'bc-live-dot stale'

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const applyFilterData = (fd) => {
    setFilters({
      excludedBookmakers:  new Set(fd.filters.excludedBookmakers  || []),
      excludedMarketTypes: new Set(fd.filters.excludedMarketTypes || []),
      excludedSports:      new Set(fd.filters.excludedSports      || []),
      odds:      fd.filters.odds      || DEFAULT_FILTERS.odds,
      ev:        fd.filters.ev        || DEFAULT_FILTERS.ev,
      hoursUntil:fd.filters.hoursUntil|| DEFAULT_FILTERS.hoursUntil,
      minBooks:  fd.filters.minBooks  ?? DEFAULT_FILTERS.minBooks,
    })
    if (fd.stakeSettings) {
      setKellyFraction(fd.stakeSettings.kellyFraction)
      setBankroll(fd.stakeSettings.bankroll)
      setRoundStakes(fd.stakeSettings.roundStakes)
    }
  }

  const handleSaveFilter = async (name) => {
    if (!name.trim()) return
    const fd = {
      filters: {
        excludedBookmakers:  [...filters.excludedBookmakers],
        excludedMarketTypes: [...filters.excludedMarketTypes],
        excludedSports:      [...filters.excludedSports],
        odds: filters.odds, ev: filters.ev, hoursUntil: filters.hoursUntil, minBooks: filters.minBooks,
      },
      stakeSettings: { kellyFraction, bankroll, roundStakes }
    }
    const { data } = await saveUserFilter(user.id, 'prop_ev', name.trim(), fd)
    if (data) setSavedFilters(prev => [...prev, { id: data.id, name: data.filter_name, isDefault: false, ...data.filter_data }])
    setFilterName('')
  }

  const handleLoadFilter = (saved) => { applyFilterData(saved); setActiveFilterId(saved.id) }

  const handleDeleteFilter = async (id) => {
    await deleteUserFilter(id)
    setSavedFilters(prev => prev.filter(f => f.id !== id))
  }

  const handleToggleDefault = async (id) => {
    const current = savedFilters.find(f => f.id === id)
    if (current?.isDefault) {
      await unsetDefaultFilter(user.id, 'prop_ev', id)
    } else {
      await setDefaultFilter(user.id, 'prop_ev', id)
    }
    setSavedFilters(prev => prev.map(f => ({ ...f, isDefault: f.id === id ? !current?.isDefault : false })))
  }

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortBy(col); setSortDir('desc') }
    setExpandedRows(new Set())
  }

  const toggleExpand = (idx) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })
  }

  // ─── Pagination ────────────────────────────────────────────────────────────

  const visibleOpps = groupedOpportunities.filter(o => !isHidden(o))

  const totalPages = Math.ceil(visibleOpps.length / itemsPerPage)
  const pagedOpps  = isPreview
    ? visibleOpps.slice(0, 4)
    : visibleOpps.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="bc-container tool-unified tool-refined propev-unified">

      {/* Header */}
      <div className="bc-header">
        <div className="bc-header-titlewrap">
          <div className="bc-header-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
              <polyline points="16 11 18 13 22 9"/>
            </svg>
          </div>
          <div className="bc-header-textcol">
            <div className="bc-header-titlerow">
              <h1 className="bc-title">Player Prop Outliers</h1>
              <button className="bc-info-btn" onClick={openPropEvTour} aria-label="How this works" title="How this works">?</button>
              <span className={dotClass} />
            </div>
          </div>
        </div>
        <div className="bc-header-actions">

          {/* Multi builder */}
          {!isPreview && (
            <button
              className={`bc-btn bc-btn-toggle${multiLegs.length > 0 ? ' toggle-active' : ''}`}
              onClick={() => setShowMultiModal(true)}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
              </svg>
              Multi
              {multiLegs.length > 0 && (
                <span className="bc-multi-count">{multiLegs.length}</span>
              )}
            </button>
          )}

          {/* Filters */}
          <button className="bc-btn" onClick={() => setShowFilterPanel(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            Filters
          </button>

          {/* Hidden bets */}
          {Object.keys(hiddenBets).length > 0 && (
            <button className="bc-btn" onClick={() => setShowModal(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
              {Object.keys(hiddenBets).length}
            </button>
          )}

          {/* Auto refresh */}
          <button
            className={`bc-btn bc-btn-toggle${autoRefreshEnabled ? ' toggle-active' : ''}`}
            onClick={() => setAutoRefreshEnabled(v => !v)}
          >
            Auto
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>

          {/* Manual refresh */}
          <button className="bc-btn bc-icon-btn" onClick={() => fetchOpportunities()} disabled={loading}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'bc-spin' : ''}>
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>

        </div>
      </div>


      {/* Error */}
      {error && <div className="bc-error">Error: {error}</div>}

      {/* Loading */}
      {loading && (
        <div className="bc-loading">
          <div className="bc-spinner" />
        </div>
      )}

      {/* Table */}
      {!loading && (
        <>
        {!noteDismissed && (
          <div className="bc-propev-note">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <span>
              <strong>For research only.</strong> Diff is calculated from over odds only, so it isn't as precise as the regular EV page. Use this tool to spot outliers, then do your own research to find the profitable bets. <strong>New here?</strong> We recommend starting with the{' '}
              <button className="bc-propev-note-link bc-propev-note-inline" onClick={() => window.dispatchEvent(new CustomEvent('bc-navigate', { detail: 'ev' }))}>EV page</button>. The work is already done there, so you can simply follow the selections.
              <button className="bc-propev-note-link" onClick={openPropEvTour}>More info</button>
            </span>
            <button className="bc-propev-note-close" onClick={dismissNote} aria-label="Dismiss">✕</button>
          </div>
        )}
        <div className="bc-table-wrapper">
          <div className="bc-table-scroll">
            <table className="bc-table">
              <thead>
                <tr>
                  {[
                    { label: '',           key: null,              sortable: false },
                    { label: 'Match',      key: 'commence_time',   sortable: true  },
                    { label: 'Market',     key: null,              sortable: false },
                    { label: 'Selection',  key: null,              sortable: false },
                    { label: 'Odds',       key: 'original_odds',   sortable: true  },
                    { label: 'Diff',       key: 'ev_percentage',   sortable: true, diffInfo: true },
                    { label: 'Stake',      key: 'kelly_stake',     sortable: true  },
                    { label: '',           key: null,              sortable: false },
                  ].map((col, i) => (
                    <th
                      key={i}
                      className={`${col.sortable ? 'sortable' : ''}${sortBy === col.key ? ' sorted' : ''}`}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {col.label}
                        {col.diffInfo && (
                          <span
                            className="bc-diff-info"
                            data-tooltip="How far this price sits from the rest of the market, after removing an assumed bookmaker margin. Treat it as a directional signal."
                            onClick={e => e.stopPropagation()}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
                            </svg>
                          </span>
                        )}
                        {col.sortable && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={sortBy === col.key ? '#60a5fa' : '#334155'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            {sortBy === col.key
                              ? (sortDir === 'desc'
                                  ? <polyline points="6 9 12 15 18 9"/>
                                  : <polyline points="18 15 12 9 6 15"/>)
                              : <><polyline points="6 9 12 4 18 9"/><polyline points="6 15 12 20 18 15"/></>
                            }
                          </svg>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedOpps.map((opp, idx) => {
                  const expanded = expandedRows.has(idx)
                  const blurRow  = isPreview && idx > 0
                  const blurCell = blurRow ? { filter: 'blur(5px)', userSelect: 'none' } : {}

                  return (
                    <React.Fragment key={idx}>
                      <tr className={`bc-row${expanded ? ' bc-row-expanded' : ''}${isInMulti(opp) ? ' bc-row-in-multi' : ''}`}>

                        {/* Multi add button */}
                        <td style={blurRow ? { filter: 'blur(5px)', userSelect: 'none' } : {}}>
                          <div className="bc-multi-btn-wrap">
                            {(() => {
                              const legIdx = multiLegs.findIndex(l => l._key === multiLegKey(opp))
                              const selected = legIdx !== -1
                              return (
                                <button
                                  className={`bc-multi-btn${selected ? ' in-multi' : ''}`}
                                  onClick={e => !isPreview && handleMultiLegClick(opp, e.currentTarget)}
                                  data-tooltip={selected ? 'Remove from multi' : 'Add to multi'}
                                  disabled={isPreview}
                                >
                                  {selected ? (
                                    <span style={{ fontSize: '11px', fontWeight: '700', lineHeight: 1 }}>{legIdx + 1}</span>
                                  ) : null}
                                </button>
                              )
                            })()}
                            {multiError?.key === multiLegKey(opp) && (
                              <div className="bc-multi-err-tip" style={{ left: multiError.x, top: multiError.y }}>
                                {multiError.msg}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Match, then sport tag + kick-off beneath it */}
                        <td style={blurCell}>
                          <div className="bc-match-name">{opp.match}</div>
                          <div className="bc-match-meta">
                            <span className="bc-sport-tag">{opp.sport || 'AFL'}</span>
                            <span className="bc-match-time">{formatDateTime(opp.commence_time)}</span>
                          </div>
                        </td>

                        {/* Market */}
                        <td style={blurCell}>
                          <span className="bc-market-text">{formatMarketType(opp.market_type)}</span>
                        </td>

                        {/* Selection */}
                        <td style={blurCell}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div className="bc-player-avatar-wrap">
                              <img
                                src={getPlayerImageUrl(opp.selection, opp.sport)}
                                alt=""
                                className={`bc-player-avatar${opp.sport === 'NRL' ? ' bc-avatar-body' : ''}`}
                                onLoad={e => { e.target.style.opacity = '1'; e.target.parentNode.classList.remove('bc-avatar-missing') }}
                                onError={e => { e.target.style.opacity = '0'; e.target.parentNode.classList.add('bc-avatar-missing') }}
                              />
                            </div>
                            {(() => {
                              const { name, dir, line } = parseSelection(opp.selection)
                              const lineParts = line ? line.match(/^([\d.]+)\s*(.*)$/) : null
                              const num  = lineParts ? lineParts[1] : line
                              const stat = lineParts ? lineParts[2] : ''
                              return (
                                <div className="bc-sel-wrap">
                                  <div className="bc-sel-name">{name}</div>
                                  {dir && (
                                    <div className={`bc-sel-badge ${dir}`} style={isPreview && idx === 0 ? { filter: 'blur(5px)', userSelect: 'none' } : {}}>
                                      <span className="bc-sel-badge-dir">{dir.charAt(0).toUpperCase() + dir.slice(1)}</span>
                                      <span className="bc-sel-badge-num">{num}</span>
                                      {stat && <span className="bc-sel-badge-stat">{stat}</span>}
                                    </div>
                                  )}
                                </div>
                              )
                            })()}
                          </div>
                        </td>

                        {/* Bookmaker(s) + odds in one cell: the price is what
                            those books are offering, so they read as a unit. */}
                        <td style={blurRow ? { filter: 'blur(5px)', userSelect: 'none' } : {}}>
                          <div className="bc-price-cell">
                          <div className="bc-book-cell">
                            {opp.bookmakers.map(b => (
                              BOOKMAKER_LOGOS[b.name] ? (
                                <span key={b.name} className="bc-book-logo-wrap" data-tooltip={b.name}>
                                  <img
                                    src={BOOKMAKER_LOGOS[b.name]}
                                    alt={b.name}
                                    className="bc-book-logo"
                                    onClick={(e) => { e.stopPropagation(); !isPreview && b.game_url && window.open(b.game_url, '_blank') }}
                                  />
                                </span>
                              ) : (
                                <span key={b.name} className="bc-book-name">{b.name}</span>
                              )
                            ))}
                          </div>
                          <div className="bc-price-odds" style={isPreview && idx === 0 ? { filter: 'blur(5px)', userSelect: 'none' } : {}}>
                            <div className="bc-odds-val">{convertOdds(opp.original_odds, oddsFormat)}</div>
                            {opp.under_odds && (
                              <div className="bc-odds-val muted">{convertOdds(opp.under_odds, oddsFormat)}</div>
                            )}
                          </div>
                          </div>
                        </td>

                        {/* Diff % */}
                        <td style={blurCell}>
                          <span className={evBadgeClass(opp)}>
                            {`+${opp.ev_percentage.toFixed(2)}%`}
                          </span>
                        </td>

                        {/* Stake */}
                        <td style={blurCell}>
                          <span className="bc-odds-val">{computeStake(opp.kelly_stake)}</span>
                        </td>

                        {/* Actions */}
                        <td style={{ ...blurCell, textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            {!isPreview && (
                              <button
                                className={`bc-expand-btn track-btn${isTracked(opp) ? ' tracked' : ''}`}
                                onClick={() => !isTracked(opp) && openTrackModal(opp)}
                                data-tooltip={isTracked(opp) ? 'Tracked' : 'Track bet'}
                              >
                                {isTracked(opp) ? (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                  </svg>
                                ) : (
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                                  </svg>
                                )}
                              </button>
                            )}
                            {!isPreview && (
                              <button
                                className="bc-expand-btn"
                                onClick={() => setShareBet(opp)}
                                data-tooltip="Share"
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="18" cy="5" r="3"/>
                                  <circle cx="6" cy="12" r="3"/>
                                  <circle cx="18" cy="19" r="3"/>
                                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                                </svg>
                              </button>
                            )}
                            {!isPreview && (
                              <button
                                className="bc-expand-btn hide-btn"
                                onClick={() => hideOpp(opp)}
                                data-tooltip="Hide"
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                  <line x1="1" y1="1" x2="23" y2="23"/>
                                </svg>
                              </button>
                            )}
                            <button
                              className={`bc-expand-btn${isPreview ? ' locked' : ''}${expanded ? ' active' : ''}`}
                              onClick={() => !isPreview && toggleExpand(idx)}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                {expanded
                                  ? <polyline points="18 15 12 9 6 15"/>
                                  : <polyline points="6 9 12 15 18 9"/>}
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>

                      {expanded && !isPreview && (() => {
                        const bookEntries = Object.entries(opp.all_bookmaker_odds || {})
                          .sort((a, b) => b[1].odds - a[1].odds)
                        const hasUnder     = bookEntries.some(([, d]) => d.under_odds)
                        const topOver      = bookEntries[0]?.[1].odds
                        const bestOverSet  = new Set(bookEntries.filter(([, d]) => d.odds === topOver).map(([b]) => b))
                        const topUnder     = hasUnder ? Math.max(...bookEntries.map(([, d]) => d.under_odds || 0)) : null
                        const bestUnderSet = hasUnder ? new Set(bookEntries.filter(([, d]) => d.under_odds === topUnder).map(([b]) => b)) : new Set()
                        return (
                          <tr>
                            <td className="bc-expanded-td" colSpan={8} style={{ maxWidth: 0 }}>
                              <div className="bc-expanded-content">
                                <div className="bc-expanded-selection">{opp.selection}</div>

                                <div className="bc-expanded-grid-scroll">
                                  <div
                                    className="bc-expanded-aligned-grid"
                                    style={{ gridTemplateColumns: `44px repeat(${bookEntries.length}, auto)` }}
                                  >
                                    {/* Over row */}
                                    <span className="bc-expanded-line-label">Over</span>
                                    {bookEntries.map(([book, data]) => (
                                      <div key={`over-${book}`} className={`bc-odds-chip${bestOverSet.has(book) ? ' is-best' : ''}`}>
                                        {BOOKMAKER_LOGOS[book]
                                          ? <img src={BOOKMAKER_LOGOS[book]} alt={book} className="bc-odds-chip-logo" data-tooltip={book} />
                                          : <span className="bc-odds-chip-name">{book}</span>}
                                        <span className="bc-odds-chip-val">{convertOdds(data.odds, oddsFormat)}</span>
                                      </div>
                                    ))}

                                    {/* Under row — always rendered */}
                                    <span className="bc-expanded-line-label">Under</span>
                                    {bookEntries.map(([book, data]) => (
                                      <div key={`under-${book}`} className={`bc-odds-chip${bestUnderSet.has(book) ? ' is-best' : ''}`}>
                                        {BOOKMAKER_LOGOS[book]
                                          ? <img src={BOOKMAKER_LOGOS[book]} alt={book} className="bc-odds-chip-logo" data-tooltip={book} />
                                          : <span className="bc-odds-chip-name">{book}</span>}
                                        <span className={data.under_odds ? 'bc-odds-chip-val' : 'bc-num-muted'}>
                                          {data.under_odds ? convertOdds(data.under_odds, oddsFormat) : '—'}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Stats */}
                                <div className="bc-expanded-stats">
                                  {typeof opp.fair_odds === 'number' && (
                                    <div className="bc-expanded-stat">
                                      <span className="bc-expanded-stat-label bc-stat-label-info">
                                        Market Avg
                                        <span className="bc-diff-info bc-stat-info" data-tooltip={MARKET_AVG_TIP}>
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
                                        </span>
                                      </span>
                                      <span className="bc-expanded-stat-val">{opp.fair_odds.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {opp.last_update && (
                                    <div className="bc-expanded-stat">
                                      <span className="bc-expanded-stat-label">Last Updated</span>
                                      <span className="bc-expanded-stat-val">{formatTimeWithSeconds(opp.last_update)}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )
                      })()}

                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>

            {/* Preview fade */}
            {isPreview && <div className="bc-preview-fade" />}
          </div>

          {/* Preview wall */}
          {isPreview && (
            <div style={{ padding: '28px 16px 36px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '100%', maxWidth: '520px' }}>
                <PreviewWall
                  title={groupedOpportunities.length > 0 ? `${groupedOpportunities.length} player prop opportunities right now` : 'player prop opportunities right now'}
                  features={['Real time odds from all major bookmakers', 'One sided expected value calculations', 'Automatic multi builder', 'Much more...']}
                  planName="Sports Premium"
                  accent="#60a5fa"
                  hadTrial={hadTrial}
                />
              </div>
            </div>
          )}

          {/* Empty */}
          {!isPreview && visibleOpps.length === 0 && !loading && (
            <div className="bc-empty">No opportunities match your filters.</div>
          )}
        </div>
        </>
      )}

      {/* Pagination — only visible once user scrolls to bottom */}
      {!isPreview && !loading && totalPages > 1 && atBottom && (
        <div className="bc-pagination">
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(1); setAtBottom(false); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === 1}
            data-tooltip="First page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 18 5 12 11 6"/><polyline points="19 18 13 12 19 6"/></svg>
          </button>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); setAtBottom(false); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === 1}
            data-tooltip="Previous page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="bc-page-info">{currentPage} / {totalPages}</span>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); setAtBottom(false); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === totalPages}
            data-tooltip="Next page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button
            className="bc-page-arrow"
            onClick={() => { setCurrentPage(totalPages); setAtBottom(false); window.scrollTo({ top: 0 }) }}
            disabled={currentPage === totalPages}
            data-tooltip="Last page"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 18 11 12 5 6"/><polyline points="13 18 19 12 13 6"/></svg>
          </button>
        </div>
      )}


      {/* Hidden Bets Modal */}
      <HiddenBetsModal
        isOpen={showModal}
        hiddenBets={hiddenBets}
        onUnhide={unhideOpp}
        onUnhideAll={clearAll}
        onClose={() => setShowModal(false)}
      />

      {/* Share Modal */}
      {shareBet && <ShareModal bet={shareBet} showMarket={false} onClose={() => setShareBet(null)} />}

      {/* Track Bet Modal */}
      <TrackBetModal
        isOpen={showTrackModal}
        opp={trackingOpp}
        form={trackForm}
        onFormChange={setTrackForm}
        onConfirm={() => saveTrack(false)}
        onConfirmAndHide={() => saveTrack(true)}
        onClose={closeTrackModal}
      />

      {/* Bookmaker Picker */}
      {pickerState && (
        <>
          <div className="bc-picker-backdrop" onClick={() => setPickerState(null)} />
          <div className="bc-picker" style={{ top: pickerState.y, left: pickerState.x }}>
            <div className="bc-picker-title">Select Bookmaker</div>
            {pickerState.opp.bookmakers.map(b => (
              <button
                key={b.name}
                className="bc-picker-option"
                onClick={() => { addMultiLeg(pickerState.opp, b.name); setPickerState(null) }}
              >
                {BOOKMAKER_LOGOS[b.name]
                  ? <img src={BOOKMAKER_LOGOS[b.name]} alt={b.name} className="bc-picker-logo" />
                  : <span style={{ width: 26 }} />}
                <span className="bc-picker-name">{b.name}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Floating multi FAB — appears once user has selected legs */}
      {!isPreview && multiLegs.length > 0 && !showMultiModal && (
        <button
          className="bc-multi-fab"
          onClick={() => setShowMultiModal(true)}
          title="Open Multi Builder"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
          <span className="bc-multi-fab-label">Multi</span>
          <span className="bc-multi-fab-count">{multiLegs.length}</span>
        </button>
      )}

      {/* Multi Builder Modal */}
      <PropEvMultiBuilder
        isOpen={showMultiModal}
        onClose={() => setShowMultiModal(false)}
        rawOpportunities={opportunities}
        selectedLegs={multiLegs}
        onLegsChange={setMultiLegs}
        kellyFraction={kellyFraction}
        bankroll={bankroll}
        roundStakes={roundStakes}
      />

      {/* Filter Panel */}
      <FilterPanel
        isOpen={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        filters={filters}
        setFilters={setFilters}
        kellyFraction={kellyFraction}
        setKellyFraction={setKellyFraction}
        bankroll={bankroll}
        setBankroll={setBankroll}
        roundStakes={roundStakes}
        setRoundStakes={setRoundStakes}
        savedFilters={savedFilters}
        activeFilterId={activeFilterId}
        filterName={filterName}
        setFilterName={setFilterName}
        onSaveFilter={handleSaveFilter}
        onLoadFilter={handleLoadFilter}
        onDeleteFilter={handleDeleteFilter}
        onToggleDefault={handleToggleDefault}
      />

      {/* Info Modal */}
      <PropEvTour isVisible={isVisible} isMobile={false} />
    </div>
  )
}
